/**
 * FlagSummary Component
 *
 * Displays a summary of flags by severity
 */

import React from 'react';
import {
  CheckCircleOutline as CleanIcon,
  ErrorOutline as CriticalIcon,
  Info as MediumIcon,
  Remove as LowIcon,
  WarningAmber as HighIcon,
} from '@mui/icons-material';
import { Box, Paper, Skeleton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { FLAG_BG_COLORS,FLAG_COLORS } from '../../constants';

interface FlagSummaryProps {
  /** Number of critical flags */
  criticalFlags?: number;
  /** Number of high flags */
  highFlags?: number;
  /** Number of medium flags */
  mediumFlags?: number;
  /** Number of low flags */
  lowFlags?: number;
  /** Number of notes with critical flags */
  notesWithCriticalFlags?: number;
  /** Number of clean notes (no flags) */
  notesClean?: number;
  /** Whether data is loading */
  isLoading?: boolean;
}

interface FlagRowProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  color: string;
  bgColor: string;
}

const FlagRow: React.FC<FlagRowProps> = ({ icon, label, count, color, bgColor }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 1.5,
        borderRadius: 1.5,
        bgcolor: bgColor,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ color, display: 'flex', alignItems: 'center' }}>{icon}</Box>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
      </Box>
      <Typography
        variant="body1"
        sx={{
          fontWeight: 700,
          color: count > 0 ? color : theme.palette.text.disabled,
        }}
      >
        {count}
      </Typography>
    </Box>
  );
};

export const FlagSummary: React.FC<FlagSummaryProps> = ({
  criticalFlags = 0,
  highFlags = 0,
  mediumFlags = 0,
  lowFlags = 0,
  notesWithCriticalFlags = 0,
  notesClean = 0,
  isLoading = false,
}) => {
  const theme = useTheme();

  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
        }}
      >
        <Skeleton width="40%" height={24} sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[1, 2, 3, 4, 5].map(i => (
            <Skeleton key={i} height={48} sx={{ borderRadius: 1.5 }} />
          ))}
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        Flag Summary
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <FlagRow
          icon={<CriticalIcon fontSize="small" />}
          label="Critical"
          count={criticalFlags}
          color={FLAG_COLORS.Critical}
          bgColor={FLAG_BG_COLORS.Critical}
        />
        <FlagRow
          icon={<HighIcon fontSize="small" />}
          label="High"
          count={highFlags}
          color={FLAG_COLORS.High}
          bgColor={FLAG_BG_COLORS.High}
        />
        <FlagRow
          icon={<MediumIcon fontSize="small" />}
          label="Medium"
          count={mediumFlags}
          color={FLAG_COLORS.Medium}
          bgColor={FLAG_BG_COLORS.Medium}
        />
        <FlagRow
          icon={<LowIcon fontSize="small" />}
          label="Low"
          count={lowFlags}
          color={FLAG_COLORS.Low}
          bgColor={FLAG_BG_COLORS.Low}
        />

        <Box sx={{ my: 1, borderTop: `1px solid ${theme.palette.grey[200]}` }} />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: theme.palette.success.light + '20',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ color: theme.palette.success.main, display: 'flex', alignItems: 'center' }}>
              <CleanIcon fontSize="small" />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Clean Notes
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
            {notesClean}
          </Typography>
        </Box>
      </Box>

      {notesWithCriticalFlags > 0 && (
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: 1.5,
            bgcolor: FLAG_BG_COLORS.Critical,
            border: `1px solid ${FLAG_COLORS.Critical}30`,
          }}
        >
          <Typography variant="body2" sx={{ color: FLAG_COLORS.Critical, fontWeight: 500 }}>
            {notesWithCriticalFlags} {notesWithCriticalFlags === 1 ? 'note' : 'notes'} with critical
            flags require attention
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default FlagSummary;

